import { useCallback, useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { resetOrderAction } from '@kfc-global/react-shared/redux/Actions/orderAction';
import { resetOrderHistory } from '@kfc-global/react-shared/redux/Actions/OrderHistoryAction';
import LazyLoaderWrapper from 'atoms/LazyLoaderWrapper';
import { UserOrderContext } from 'context/context';
import { LOCAL_STORAGE_KEYS, RESIZE, IMAGE_SIZE_CONSTANTS } from 'common/constants/SharedConstants';
import { getPercentage, getTernaryValue, isMobileDevice, isValidObject } from 'common/utilities/utils';

import {
  defaultOrderHistoryDetails,
  getOrderHistoryAction,
  getOrdersStatusAction,
  updateOrderHistoryDetailsFromOrderHistory,
  updateOrderHistoryDetailsFromOrderHistoryReducer,
  updateOrderHistoryDetailsFromOrderReducer,
  updateOrderHistoryDetailsFromTenantProfileReducer,
  updateOrderHistoryDetailsFromCartReducer,
  updateOrderHistoryDetailsFromAppStateReducer,
  updateOrderHistoryDetailsFromPaymentsContentReducer,
} from '../utils/orderHistoryUtils';
import { dispatchGetTendersContent } from 'common/utilities/orderProcessingSummaryUtils';

const getOrderHistoryView = params => {
  return (
    <LazyLoaderWrapper {...params} testId='order_history_view' importPath='ORDER_HISTORY_VIEW'></LazyLoaderWrapper>
  );
};

const OrderHistory = props => {
  const dispatch = useDispatch();
  const { userOrderState, userOrderStateDispatch } = useContext(UserOrderContext);
  const {
    tenantReducer,
    orderHistoryReducer,
    orderReducer,
    profileReducer,
    cartReducer,
    paymentsContentReducer,
    appStateReducer,
  } = useSelector(data => data);
  const [orderHistoryDetails, setOrderHistoryDetails] = useState(defaultOrderHistoryDetails);
  const [imageOrderContainerSize, setImageOrderContainerSize] = useState({ height: 0, width: 0 });
  const { ORDER_HISTORY_ITEM } = IMAGE_SIZE_CONSTANTS;
  const { ITEM_IMAGE_HEIGHT_MOBILE, ITEM_IMAGE_WIDTH_MOBILE, ITEM_WIDTH_DESK_PRCNT } = ORDER_HISTORY_ITEM;

  const updateSetOrderHistoryDetails = useCallback(param => {
    setOrderHistoryDetails && setOrderHistoryDetails(prevState => ({ ...prevState, ...param }));
  }, []);

  const resizeHandler = () => {
    const isMobileView = isMobileDevice(window.innerWidth);
    updateSetOrderHistoryDetails({ isMobileView });
    const size = getTernaryValue(
      isMobileView,
      { height: ITEM_IMAGE_HEIGHT_MOBILE, width: ITEM_IMAGE_WIDTH_MOBILE },
      {
        height: getPercentage(window.innerWidth, ITEM_WIDTH_DESK_PRCNT),
        width: getPercentage(window.innerWidth, ITEM_WIDTH_DESK_PRCNT),
      },
    );
    setImageOrderContainerSize(size);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener(RESIZE, resizeHandler);
    resizeHandler();
    return () => {
      dispatch(resetOrderHistory());
      localStorage.removeItem(LOCAL_STORAGE_KEYS?.LOAD_MORE_ORDER_HISTORY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatchGetTendersContent(dispatch);
  }, [dispatch]);

  /*********************************************************************
   *
   *  console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    if (tenantReducer?.tenantId && profileReducer?.customerId) {
      updateOrderHistoryDetailsFromTenantProfileReducer({
        props,
        dispatch,
        tenantReducer,
        profileReducer,
        updateSetOrderHistoryDetails,
        openReorderModal: userOrderState?.reorderFlag?.reorderModalOpen,
        userOrderStateDispatch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantReducer?.tenantId, profileReducer?.customerId, userOrderState?.reorderFlag?.reorderModalOpen]);

  /*********************************************************************
   * useEffect
   * appStateReducer?.userStatus
   * console.debug('useEffect appStateReducer => ',appStateReducer);
   *********************************************************************/
  useEffect(() => {
    if (appStateReducer?.userStatus) {
      updateOrderHistoryDetailsFromAppStateReducer({
        appStateReducer,
        updateSetOrderHistoryDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStateReducer?.userStatus]);

  /*********************************************************************
   * useEffect
   * paymentsContentReducer
   * console.debug('useEffect paymentsContentReducer => ', paymentsContentReducer);
   *********************************************************************/
  useEffect(() => {
    if (paymentsContentReducer) {
      updateOrderHistoryDetailsFromPaymentsContentReducer({
        paymentsContentReducer,
        updateSetOrderHistoryDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsContentReducer]);

  /*********************************************************************
   *
   *  console.debug('useEffect cartReducer => ', cartReducer?.cartData?.id);
   *********************************************************************/
  useEffect(() => {
    if (cartReducer?.cartData && cartReducer?.cartData?.id) {
      updateOrderHistoryDetailsFromCartReducer({
        cartReducer,
        updateSetOrderHistoryDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReducer?.cartData]);

  useEffect(() => {
    orderHistoryDetails?.tenantId && getOrderHistoryAction(orderHistoryDetails);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryDetails?.tenantId]);

  useEffect(() => {
    orderHistoryReducer?.orderHistory &&
      profileReducer?.customerId &&
      !orderHistoryDetails?.showFavoriteModal &&
      updateOrderHistoryDetailsFromOrderHistoryReducer({
        orderHistoryReducer,
        orderHistoryDetails,
        profileReducer,
        updateSetOrderHistoryDetails,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryReducer?.orderHistory, profileReducer?.favouriteOrders]);

  useEffect(() => {
    updateSetOrderHistoryDetails({ orderHistoryLoading: orderHistoryReducer?.orderHistoryLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryReducer?.orderHistoryLoading]);

  useEffect(() => {
    isValidObject(orderReducer?.response) &&
      updateOrderHistoryDetailsFromOrderReducer({
        orderReducer,
        orderHistoryDetails,
        updateSetOrderHistoryDetails,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderReducer?.response]);

  useEffect(() => {
    orderHistoryDetails?.orderHistory?.length > 0 &&
      updateOrderHistoryDetailsFromOrderHistory({
        orderHistoryDetails,
        updateSetOrderHistoryDetails,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryDetails?.orderHistory]);

  useEffect(() => {
    orderHistoryDetails?.selectedOrderId ? getOrdersStatusAction(orderHistoryDetails) : dispatch(resetOrderAction());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryDetails?.selectedOrderId]);

  useEffect(() => {
    orderHistoryDetails?.completedOrderHistory?.length > 0 &&
      updateSetOrderHistoryDetails({
        totalNumOfPages: Math.ceil(
          orderHistoryDetails?.completedOrderHistory?.length / orderHistoryDetails?.displayNumOfRecords,
        ),
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryDetails?.completedOrderHistory]);

  return getTernaryValue(
    imageOrderContainerSize.height,
    getOrderHistoryView({
      orderHistoryDetails,
      updateSetOrderHistoryDetails,
      imageOrderContainerSize,
      userParams: appStateReducer?.userStatus,
      tenantReducer,
    }),
    <></>,
  );
};

export default OrderHistory;
